import { FieldValues, UseFormReturn, Controller } from 'react-hook-form';
import { useState, useRef, useEffect } from 'react';

interface FilterDropdownProps {
  name: string;
  placeholder: string;
  options: { label: string; value: string; icon?: React.ReactNode }[];
  formHook: UseFormReturn<FieldValues>;
  multiSelect?: boolean;
}

export const FilterDropdown = ({ name, placeholder, options, formHook, multiSelect = false }: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Controller
      control={formHook.control}
      name={name}
      render={({ field: { onChange, value } }) => {
        const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
        const displayText = selectedValues.length > 0
          ? selectedValues.map(v => options.find(option => option.value === v)?.label).join(', ')
          : placeholder;

        const handleOptionClick = (optionValue: string) => {
          if (multiSelect) {
            const newValues = selectedValues.includes(optionValue)
              ? selectedValues.filter(v => v !== optionValue)
              : [...selectedValues, optionValue];
            onChange(newValues);
          } else {
            onChange(optionValue);
            setIsOpen(false);
          }
        };

        return (
          <div className="relative" ref={dropdownRef}>
            <div
              className={`bg-gray-100 border-0 rounded-lg px-3 py-2.5 pr-8 text-gray-600 placeholder:text-gray-500 focus:bg-gray-200 focus:outline-none text-sm cursor-pointer min-w-[200px] flex items-center justify-between ${
                isOpen ? 'ring-2 ring-[#52B6FE]' : ''
              }`}
              onClick={() => setIsOpen(!isOpen)}
            >
              <span className="truncate">{displayText}</span>
              {isOpen ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-gray-600"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-gray-600"
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>

            {isOpen && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                {options.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center gap-3 px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 cursor-pointer first:rounded-t-lg last:rounded-b-lg"
                    onClick={() => handleOptionClick(option.value)}
                  >
                    {option.icon && (
                      <div className="w-5 h-5 flex items-center justify-center text-gray-600">
                        {option.icon}
                      </div>
                    )}
                    <span className="flex-1">{option.label}</span>
                    {multiSelect && (
                      <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                        selectedValues.includes(option.value)
                          ? 'bg-blue-500 border-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedValues.includes(option.value) && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }}
    />
  );
};
